import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api } from "../../api";
import { useAuth } from "../../AuthContext";
import { APageHead } from "../adminKit";

export default function AdminSettings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const isSuper = user?.role === "super_admin";

  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)); }, []);
  if (!s) return <div className="text-slate-500">Loading…</div>;

  const save = async () => {
    if (!isSuper) return toast.error("Only super admins can modify settings");
    try {
      await api.put("/admin/settings", { settings: { general: s.general, security: s.security, feature_flags: s.feature_flags } });
      toast.success("Settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setFlag = (k) => setS({ ...s, feature_flags: { ...s.feature_flags, [k]: !s.feature_flags[k] } });
  const setGen = (k, v) => setS({ ...s, general: { ...s.general, [k]: v } });

  return (
    <div className="space-y-5 max-w-4xl">
      <APageHead title="System Settings" subtitle={isSuper ? "Manage platform settings and feature flags." : "Read-only — only super admins can modify settings."}
        action={<button onClick={save} disabled={!isSuper} className="cc-btn-primary inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-500 disabled:opacity-40" data-testid="settings-save"><Save size={15} /> Save</button>} />

      <div className="cc-card rounded-2xl p-6">
        <h3 className="cc-heading text-base font-500 text-white mb-4">General</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {["platform_name", "support_email", "timezone", "currency"].map((k) => (
            <div key={k}>
              <label className="text-xs uppercase tracking-widest text-slate-500">{k.replace("_", " ")}</label>
              <input value={s.general[k] || ""} disabled={!isSuper} onChange={(e) => setGen(k, e.target.value)} className="cc-input w-full rounded-xl px-4 py-2.5 text-sm mt-1.5 disabled:opacity-60" data-testid={`setting-${k}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="cc-card rounded-2xl p-6">
        <h3 className="cc-heading text-base font-500 text-white mb-4">Feature Flags</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(s.feature_flags).map(([k, v]) => (
            <button key={k} onClick={() => isSuper && setFlag(k)} disabled={!isSuper}
              className="flex items-center justify-between rounded-xl cc-surface border border-white/[0.05] px-4 py-3 text-left disabled:opacity-70" data-testid={`flag-${k}`}>
              <span className="text-sm text-slate-200 capitalize">{k.replace(/_/g, " ")}</span>
              <span className={`relative h-5 w-9 rounded-full transition-colors ${v ? "cc-gradient" : "cc-surface-2 border border-white/10"}`}>
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${v ? "left-[18px]" : "left-0.5"}`} />
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="cc-card rounded-2xl p-6">
        <h3 className="cc-heading text-base font-500 text-white mb-4">Security</h3>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <Row l="Session duration (days)" v={s.security.session_days} />
          <Row l="Admin session (hours)" v={s.security.admin_session_hours} />
          <Row l="Login attempt limit" v={s.security.login_attempt_limit} />
          <Row l="Admin 2FA required" v={s.security.admin_2fa_required ? "Yes (prepared)" : "Not yet enforced"} />
          <Row l="IP logging" v={s.security.ip_logging ? "Enabled" : "Disabled"} />
        </div>
      </div>
    </div>
  );
}

const Row = ({ l, v }) => (
  <div className="flex items-center justify-between rounded-lg cc-surface border border-white/[0.05] px-4 py-3">
    <span className="text-slate-400">{l}</span><span className="text-slate-200">{v}</span>
  </div>
);
